import { useEffect, useRef } from "react";

// Inline open-simplex 2D noise (no npm dependency)
const STRETCH_2D = (1 / Math.sqrt(2 + 1) - 1) / 2;
const SQUISH_2D = (Math.sqrt(2 + 1) - 1) / 2;
const NORM_2D = 47;
const GRADIENTS_2D = [5, 2, 2, 5, -5, 2, -2, 5, 5, -2, 2, -5, -5, -2, -2, -5];

function createNoise2D(seed) {
  const perm = new Uint8Array(256);
  const source = new Uint8Array(256);
  for (let i = 0; i < 256; i++) source[i] = i;
  let s = seed;
  for (let i = 255; i >= 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    if (s < 0) s += 2147483647;
    const r = ((s + 31) % (i + 1) + i + 1) % (i + 1);
    perm[i] = source[r];
    source[r] = source[i];
  }

  return function noise2D(x, y) {
    const stretchOffset = (x + y) * STRETCH_2D;
    const xs = x + stretchOffset;
    const ys = y + stretchOffset;
    const xsb = Math.floor(xs);
    const ysb = Math.floor(ys);
    const squishOffset = (xsb + ysb) * SQUISH_2D;
    const dx0 = x - (xsb + squishOffset);
    const dy0 = y - (ysb + squishOffset);
    const xins = xs - xsb;
    const yins = ys - ysb;
    const inSum = xins + yins;

    let value = 0;
    let attn0 = 2 - dx0 * dx0 - dy0 * dy0;
    if (attn0 > 0) { attn0 *= attn0; const gi0 = perm[(perm[xsb & 0xff] + ysb) & 0xff] % 8 * 2; value += attn0 * attn0 * (GRADIENTS_2D[gi0] * dx0 + GRADIENTS_2D[gi0 + 1] * dy0); }
    const dx1 = dx0 - 1 - SQUISH_2D;
    const dy1 = dy0 - 0 - SQUISH_2D;
    let attn1 = 2 - dx1 * dx1 - dy1 * dy1;
    if (attn1 > 0) { attn1 *= attn1; const gi1 = perm[(perm[(xsb + 1) & 0xff] + ysb) & 0xff] % 8 * 2; value += attn1 * attn1 * (GRADIENTS_2D[gi1] * dx1 + GRADIENTS_2D[gi1 + 1] * dy1); }
    const dx2 = dx0 - 0 - SQUISH_2D;
    const dy2 = dy0 - 1 - SQUISH_2D;
    let attn2 = 2 - dx2 * dx2 - dy2 * dy2;
    if (attn2 > 0) { attn2 *= attn2; const gi2 = perm[(perm[xsb & 0xff] + ysb + 1) & 0xff] % 8 * 2; value += attn2 * attn2 * (GRADIENTS_2D[gi2] * dx2 + GRADIENTS_2D[gi2 + 1] * dy2); }
    if (inSum > 1) {
      const dx3 = dx0 - 1 - 2 * SQUISH_2D;
      const dy3 = dy0 - 1 - 2 * SQUISH_2D;
      let attn3 = 2 - dx3 * dx3 - dy3 * dy3;
      if (attn3 > 0) { attn3 *= attn3; const gi3 = perm[(perm[(xsb + 1) & 0xff] + ysb + 1) & 0xff] % 8 * 2; value += attn3 * attn3 * (GRADIENTS_2D[gi3] * dx3 + GRADIENTS_2D[gi3 + 1] * dy3); }
    }
    return value / NORM_2D;
  };
}

const TWO_PI = Math.PI * 2;

/**
 * Canvas-based speckled gradient background.
 * @param {object} props
 * @param {"light"|"dark"} [props.variant="light"]
 * @param {boolean} [props.animate=true] - When false, renders a single static frame.
 */
export function SpeckledGradient({ variant = "light", animate = true }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const shouldAnimate = animate && !reducedMotion;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const noise2D = createNoise2D(42);

    // Secondary (blue) colors — with faded depth variants
    const lightSecondaryColors = [
      { color: "#5382a6", weight: 0.46 },
      { color: "#3d6a8e", weight: 0.24 },
      { color: "#6a97b8", weight: 0.14 },
      { color: "#89b3cf", weight: 0.05 },
      { color: "#a2c4da", weight: 0.04 },
    ];
    // Primary (yellow-green) — single color, no faded variants
    const PRIMARY_COLOR = "#DFE633";
    const PRIMARY_CHANCE = 0.07; // ~7% of dots

    const darkSecondaryColors = [
      { color: "#3d5a73", weight: 0.46 },
      { color: "#2c4a63", weight: 0.24 },
      { color: "#4d6f8a", weight: 0.18 },
      { color: "#1e3a50", weight: 0.12 },
    ];
    const DARK_PRIMARY_COLOR = "#9ba018";

    const secondaryColors = variant === "dark" ? darkSecondaryColors : lightSecondaryColors;
    const primaryColor = variant === "dark" ? DARK_PRIMARY_COLOR : PRIMARY_COLOR;

    function pickSecondaryColor() {
      const r = Math.random();
      let cumulative = 0;
      for (const c of secondaryColors) {
        cumulative += c.weight;
        if (r <= cumulative) return c.color;
      }
      return secondaryColors[0].color;
    }

    function generateDots(w, h) {
      const dots = [];
      const viewportWidth = w / dpr;

      // Heavier density
      let targetCount;
      if (viewportWidth >= 1024) targetCount = 14000;
      else if (viewportWidth >= 640) targetCount = 8000;
      else targetCount = 4000;

      const cols = Math.ceil(Math.sqrt(targetCount * (w / h)));
      const rows = Math.ceil(targetCount / cols);

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const cellW = w / cols;
          const cellH = h / rows;
          const x = col * cellW + Math.random() * cellW;
          const y = row * cellH + Math.random() * cellH;

          if (x > w || y > h) continue;

          // Vertical band centered behind the logo (~33% from left)
          const nx = x / w;
          const center = 0.33;
          const dist = Math.abs(nx - center);

          // Gentle bell-curve: strongest at center, gradual falloff to edges
          const baseOpacity = Math.pow(Math.max(0, 1 - dist * 1.3), 1.4) * 0.75;
          if (baseOpacity < 0.03) continue;

          // Gently thin out dots far from the center band
          if (dist > 0.45 && Math.random() > (1 - dist * 0.8)) continue;

          const isPrimary = Math.random() < PRIMARY_CHANCE;

          // Primary dots: full opacity, no fading. Secondary dots: use gradient opacity.
          const opacity = isPrimary ? Math.min(baseOpacity + 0.3, 0.85) : baseOpacity;
          const color = isPrimary ? primaryColor : pickSecondaryColor();

          const isAnchor = Math.random() < 0.04;
          const baseRadius = isAnchor ? (1.6 + Math.random() * 0.3) : (0.6 + Math.random() * 0.5);
          const radius = baseRadius * (0.6 + opacity * 0.5);

          dots.push({
            baseX: x,
            baseY: y,
            radius: radius * dpr,
            color,
            opacity,
          });
        }
      }

      return dots;
    }

    function resize() {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const w = Math.round(rect.width * dpr);
      const h = Math.round(rect.height * dpr);
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const dots = generateDots(w, h);
      if (stateRef.current) {
        stateRef.current.dots = dots;
      }
      return dots;
    }

    // Lens distortion parameters
    const LENS_RADIUS = 150 * dpr;
    const LENS_RADIUS_SQ = LENS_RADIUS * LENS_RADIUS;
    const LENS_STRENGTH = 12 * dpr; // subtle warp, not a clearing effect

    function renderFrame(c, d, offset) {
      c.clearRect(0, 0, canvas.width, canvas.height);

      const mouse = mouseRef.current;
      const mx = mouse.x * dpr;
      const my = mouse.y * dpr;
      const mouseActive = mouse.active;

      for (const dot of d) {
        // Directional flow field — creates water-current-like movement
        const angle = noise2D(dot.baseX * 0.002, dot.baseY * 0.002 + offset) * TWO_PI;
        const displacement = 6 * dpr;

        // Lateral streaming — a second noise layer pushes dots horizontally
        const lateral = noise2D(dot.baseX * 0.001 + 500, dot.baseY * 0.0015 + offset * 3) * 20 * dpr;
        const drift = noise2D(dot.baseX * 0.0008 + 1000, dot.baseY * 0.001 + offset * 1.5) * 8 * dpr;

        let x = dot.baseX + Math.cos(angle) * displacement + lateral;
        let y = dot.baseY + Math.sin(angle) * displacement + drift;

        // Lens distortion — push dots away from cursor
        if (mouseActive) {
          const dx = x - mx;
          const dy = y - my;
          const distSq = dx * dx + dy * dy;
          if (distSq < LENS_RADIUS_SQ && distSq > 0) {
            const dist = Math.sqrt(distSq);
            const t = 1 - dist / LENS_RADIUS; // 1 at center, 0 at edge
            const push = t * t * LENS_STRENGTH; // quadratic falloff
            x += (dx / dist) * push;
            y += (dy / dist) * push;
          }
        }

        c.globalAlpha = dot.opacity;
        c.fillStyle = dot.color;
        c.beginPath();
        c.arc(Math.round(x), Math.round(y), dot.radius, 0, TWO_PI);
        c.fill();
      }
      c.globalAlpha = 1;
    }

    const dots = resize() || [];

    stateRef.current = {
      dots,
      animId: 0,
      noise2D,
      isVisible: true,
      currentOffset: 0,
    };

    // Render initial static frame
    renderFrame(ctx, dots, 0);

    // Static mode or reduced motion — handle resize + mouse lens
    if (!shouldAnimate) {
      let staticAnimId;
      const handleStaticMouse = (e) => {
        const rect = canvas.getBoundingClientRect();
        mouseRef.current.x = e.clientX - rect.left;
        mouseRef.current.y = e.clientY - rect.top;
        mouseRef.current.active = true;
        cancelAnimationFrame(staticAnimId);
        staticAnimId = requestAnimationFrame(() => {
          if (stateRef.current) renderFrame(ctx, stateRef.current.dots, 0);
        });
      };
      const handleStaticLeave = () => {
        mouseRef.current.active = false;
        cancelAnimationFrame(staticAnimId);
        staticAnimId = requestAnimationFrame(() => {
          if (stateRef.current) renderFrame(ctx, stateRef.current.dots, 0);
        });
      };
      let resizeTimer;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          resize();
          if (stateRef.current) renderFrame(ctx, stateRef.current.dots, 0);
        }, 250);
      };
      window.addEventListener("mousemove", handleStaticMouse);
      window.addEventListener("mouseleave", handleStaticLeave);
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("mousemove", handleStaticMouse);
        window.removeEventListener("mouseleave", handleStaticLeave);
        window.removeEventListener("resize", handleResize);
        cancelAnimationFrame(staticAnimId);
        clearTimeout(resizeTimer);
      };
    }

    // Time-based continuous animation — visible water-current drift
    const FRAME_INTERVAL = 1000 / 30; // 30fps cap
    let lastFrame = 0;

    function animateLoop(timestamp) {
      const state = stateRef.current;
      if (!state) return;

      state.animId = requestAnimationFrame(animateLoop);

      if (!state.isVisible) return;
      if (timestamp - lastFrame < FRAME_INTERVAL) return;
      lastFrame = timestamp;

      // Visible drift speed — enough to see currents flowing
      state.currentOffset += 0.004;
      renderFrame(ctx, state.dots, state.currentOffset);
    }

    stateRef.current.animId = requestAnimationFrame(animateLoop);

    // Pause when offscreen
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (stateRef.current) {
          stateRef.current.isVisible = entry.isIntersecting;
        }
      },
      { threshold: 0 },
    );
    observer.observe(canvas);

    // Mouse tracking for lens distortion
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    };
    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    // Debounced resize
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resize();
        if (stateRef.current) {
          renderFrame(ctx, stateRef.current.dots, stateRef.current.currentOffset);
        }
      }, 250);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      if (stateRef.current) {
        cancelAnimationFrame(stateRef.current.animId);
      }
      observer.disconnect();
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [variant, animate]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}

export default SpeckledGradient;
